import * as React from "react"
import { Bold, Italic, Underline, List, ListOrdered, Link2, Link2Off } from "lucide-react"

import { cn } from "@/lib/utils"

// Kept in step with ALLOWED_TAGS in backend/email_html.py — anything the toolbar
// produces beyond that list is stripped server-side before the email is sent.
const COMMANDS = [
  { id: "bold", command: "bold", label: "Bold", Icon: Bold },
  { id: "italic", command: "italic", label: "Italic", Icon: Italic },
  { id: "underline", command: "underline", label: "Underline", Icon: Underline },
  { id: "insertUnorderedList", command: "insertUnorderedList", label: "Bulleted list", Icon: List },
  { id: "insertOrderedList", command: "insertOrderedList", label: "Numbered list", Icon: ListOrdered },
]

function normalizeUrl(value) {
  const url = String(value || "").trim()
  if (!url) return ""
  if (/^(https?:|mailto:)/i.test(url)) return url
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(url)) return `mailto:${url}`
  return `https://${url}`
}

const RichTextEditor = React.forwardRef(({ value, onChange, disabled, className, ...props }, ref) => {
  const editorRef = React.useRef(null)
  const [activeCommands, setActiveCommands] = React.useState({})

  React.useImperativeHandle(ref, () => editorRef.current, [])

  // contentEditable owns its DOM, so only write into it when the incoming value
  // genuinely differs — otherwise every keystroke would reset the caret.
  //
  // `value` is assigned to innerHTML, so it must only ever come from a trusted
  // producer: the server-rendered template (which HTML-escapes every applicant
  // field it interpolates) or this editor's own output. Do not pass raw HTML
  // from an untrusted source here without sanitising it first.
  React.useEffect(() => {
    const node = editorRef.current
    if (!node) return
    const next = value || ""
    if (node.innerHTML !== next) node.innerHTML = next
  }, [value])

  const emitChange = React.useCallback(() => {
    const node = editorRef.current
    if (!node || !onChange) return
    onChange({ html: node.innerHTML, text: node.innerText })
  }, [onChange])

  const refreshActiveCommands = React.useCallback(() => {
    if (disabled) return
    const node = editorRef.current
    if (!node || document.activeElement !== node) return
    const next = {}
    COMMANDS.forEach(({ id, command }) => {
      try {
        next[id] = document.queryCommandState(command)
      } catch {
        next[id] = false
      }
    })
    setActiveCommands(next)
  }, [disabled])

  React.useEffect(() => {
    document.addEventListener("selectionchange", refreshActiveCommands)
    return () => document.removeEventListener("selectionchange", refreshActiveCommands)
  }, [refreshActiveCommands])

  const runCommand = (command, argument) => {
    if (disabled) return
    const node = editorRef.current
    if (!node) return
    node.focus()
    document.execCommand(command, false, argument)
    emitChange()
    refreshActiveCommands()
  }

  const applyLink = () => {
    const selection = window.getSelection()
    if (!selection || selection.isCollapsed) {
      window.alert("Select the text you want to turn into a link first.")
      return
    }
    const entered = window.prompt("Link address", "https://")
    if (entered === null) return
    const url = normalizeUrl(entered)
    if (!url) return
    runCommand("createLink", url)
  }

  // Pasting from Word or a browser drags in fonts, colours and tracking markup.
  // Taking the plain text keeps the message looking like the rest of the email.
  const handlePaste = (event) => {
    event.preventDefault()
    const text = event.clipboardData?.getData("text/plain") || ""
    document.execCommand("insertText", false, text)
    emitChange()
  }

  return (
    <div
      className={cn(
        "rounded-md border border-input bg-white shadow-sm focus-within:ring-1 focus-within:ring-ring",
        disabled && "opacity-50",
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-1 border-b border-[rgba(6,43,26,0.10)] px-2 py-1.5">
        {COMMANDS.map(({ id, command, label, Icon }) => (
          <button
            key={id}
            type="button"
            title={label}
            aria-label={label}
            aria-pressed={Boolean(activeCommands[id])}
            disabled={disabled}
            // onMouseDown, not onClick: clicking a button would blur the editor
            // and collapse the selection before the command could apply.
            onMouseDown={(event) => {
              event.preventDefault()
              runCommand(command)
            }}
            className={cn(
              "inline-flex h-8 w-8 items-center justify-center rounded text-[#062B1A]/70 transition-colors hover:bg-[#F1F3F5] hover:text-[#062B1A] disabled:cursor-not-allowed",
              activeCommands[id] && "bg-[#062B1A] text-white hover:bg-[#062B1A] hover:text-white",
            )}
          >
            <Icon className="h-4 w-4" />
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-[rgba(6,43,26,0.12)]" />
        <button
          type="button"
          title="Add link"
          aria-label="Add link"
          disabled={disabled}
          onMouseDown={(event) => {
            event.preventDefault()
            applyLink()
          }}
          className="inline-flex h-8 w-8 items-center justify-center rounded text-[#062B1A]/70 transition-colors hover:bg-[#F1F3F5] hover:text-[#062B1A] disabled:cursor-not-allowed"
        >
          <Link2 className="h-4 w-4" />
        </button>
        <button
          type="button"
          title="Remove link"
          aria-label="Remove link"
          disabled={disabled}
          onMouseDown={(event) => {
            event.preventDefault()
            runCommand("unlink")
          }}
          className="inline-flex h-8 w-8 items-center justify-center rounded text-[#062B1A]/70 transition-colors hover:bg-[#F1F3F5] hover:text-[#062B1A] disabled:cursor-not-allowed"
        >
          <Link2Off className="h-4 w-4" />
        </button>
      </div>

      <div
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        onInput={emitChange}
        onBlur={emitChange}
        onPaste={handlePaste}
        onKeyUp={refreshActiveCommands}
        onMouseUp={refreshActiveCommands}
        // Tailwind's preflight strips list styling, so restore it for the
        // editor's own content only.
        className={cn(
          "min-h-[260px] w-full overflow-y-auto px-3 py-2 text-sm leading-relaxed text-[#062B1A] focus:outline-none",
          "[&_p]:my-2 [&_a]:text-[#0B5FBF] [&_a]:underline",
          "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1",
        )}
        {...props}
      />
    </div>
  )
})
RichTextEditor.displayName = "RichTextEditor"

export { RichTextEditor }
